/**
 * Lib.Common: Number Formatting
 */


import $locale from './locale';


const Intl = window.Intl;
const emDash = '—';


const format = function(num) {
    return num === '' ? num : isNaN(num) ? emDash : this.format(num);
}

const formatToParts = function(num) {
    return num === '' ? [] : isNaN(num) ? [{ type: 'nan', value: emDash }] : this.formatToParts(num);
}


const $num = {

    /**
     * Convert a number to a locale formatted string.
     *   `$num.int`:              Integer portion only (5,001).
     *   `$num.decimal`:          Unlimited decimal places (5,000.50005).
     *   `$num.fixed`:            Exactly 2 decimal places (5,000.50).
     *   `$num.fixed1`:           Exactly 1 decimal place (5,000.5).
     *   `$num.fixed3`:           Exactly 3 decimal places (5,000.500).
     *   `$num.fixed4`:           Exactly 4 decimal places (5,000.5001).
     *   `$num.fixed5`:           Exactly 5 decimal places (5,000.50005).
     *   `$num.fixed6`:           Exactly 6 decimal places (5,000.500050).
     *   `$num.min2`:             At least 2 decimal places (5,000.50005).
     *   `$num.max4`:             Up to 4 decimal places (5,000.5001).
     *   `$num.percent`:          Percentage, with no decimal places (500,050%).
     *   `$num.percent1`:         Percentage, with 1 decimal place (500,050.0%).
     *   `$num.percent2`:         Percentage, with 2 decimal places (500,050.00%).
     *   `$num.raw`:              Preformatted number; pass through or display em dash if undefined.
     *   `$num.bytes`:            In kB, MB, GB, or TB as appropriate, with at least 2 significant digits.
     *
     * Each of these shortcuts offers the `.flat` or `.sign` options - for example:
     *   `$num.decimal.flat`:     Without thousands grouping (5000.50005).
     *   `$num.decimal.sign`:     With sign if nonzero (+5,000.50005).
     *
     * These also offer `.compact` and `.flex` options. These options also themselves support `.flat` and
     * `.sign` modifiers.
     *   `$num.fixed.compact`:    Two decimal places if that will fit within 6 digits; no decimal places if below 1M;
     *                            otherwise, use compact notation with exactly 3 significant digits (1.23M).
     *   `$num.fixed.flex`:       Two decimal places if value will round to below 1M; otherwise zero decimal places.
     *
     * All of the above support the `.parts` modifier. The `.parts` modifier generates an array of objects, each of
     * which has `type` and `value` properties. This can be useful if you need to parse the result in a locale
     * independent way. The types are:
     *   `minusSign`:             A minus sign.
     *   `plusSign`:              A plus sign.
     *   `integer`:               A group of integer digits (before the decimal point).
     *   `fraction`:              A group of fractional digits (after the decimal point).
     *   `decimal`:               The decimal point.
     *   `group`:                 The group separator.
     *   `percent`:               A percent sign.
     *   `literal`:               A literal string, typically used for a non-breaking space.
     *   `compact`:               An abbreviation for compact formatting.
     *   `unit`:                  A unit descriptor (kB, MB, GB, TB) for units formatting.
     *   `nan`:                   Indicates NaN (not a number).
     *   `infinity`:              Indicates Infinity.
     *
     * To use one of the shortcut methods, you can simply put in your template something like:
     *   `div {{ $num.fixed.sign(value) }}`
     *
     * To define your own formatter, put it in your component's methods:
     *   `methods: { formatter: $num.format({ maximumFractionDigits: 2, useGrouping: false }) }`
     *
     * and in your template:
     *    `div(v-text="formatter(value)")`
     */

    _use(language) {
        const formatter = options => {
            const self = new window.Intl.NumberFormat(language, options);
            const fn = format.bind(self);
            fn.parts = formatToParts.bind(self);
            return fn;
        }

        const flexFormatter = (digits, alt1, alt2) => {
            const threshold = 1e6 - .5 / 10 ** digits;
            const fn = num =>
                num < threshold && num > -threshold ? alt1(num) : alt2(num);
            return fn;
        }

        const compactFormatter = (formatter, digits, alt1, alt2) => {
            digits = Math.min(5, digits);
            const threshold1 = 10 ** (6 - digits) - .5 / 10 ** digits;
            const threshold2 = 1e6 - .5 / 10 ** digits;
            const fn = num =>
                num < threshold1 && num > -threshold1 ? alt1(num) :
                num < threshold2 && num > -threshold2 ? alt2(num) :
                formatter(num);
            fn.parts = num =>
                num < threshold1 && num > -threshold1 ? alt1.parts(num) :
                num < threshold2 && num > -threshold2 ? alt2.parts(num) :
                formatter.parts(num);
            return fn;
        }

        this.formatter = options => formatter(options);

        const base = [
            ['int', 0, 0],
            ['decimal', 0, 20],
            ['fixed', 2, 2,, 1],
            ['fixed1', 1, 1,, 1],
            ['fixed3', 3, 3,, 1],
            ['fixed4', 4, 4,, 1],
            ['fixed5', 5, 5,, 1],
            ['fixed6', 6, 6,, 1],
            ['min2', 2, 20],
            ['max4', 0, 4],
            ['percent', 0, 0, { style: 'percent' }],
            ['percent1', 1, 1, { style: 'percent' }],
            ['percent2', 2, 2, { style: 'percent' }],
        ];

        const compact = {
            numberingSystem: 'latn',
            notation: 'compact',
            minimumSignificantDigits: 3,
            maximumSignificantDigits: 3
        };
        const compactFmt = formatter(compact);
        const compactFlat = formatter({ ...compact, useGrouping: false });
        const compactSign = formatter({ ...compact, signDisplay: 'exceptZero' });

        base.forEach(mode => {
            const options = {
                numberingSystem: 'latn',
                minimumFractionDigits: mode[1],
                maximumFractionDigits: mode[2],
                ...mode[3]
            };
            $num[mode[0]] = formatter(options);
            $num[mode[0]].flat = formatter({ ...options, useGrouping: false });
            $num[mode[0]].sign = formatter({ ...options, signDisplay: 'exceptZero' });
            $num[mode[0]].flex = flexFormatter(mode[2], $num[mode[0]], $num.int);
            $num[mode[0]].compact = compactFormatter(compactFmt, mode[2], $num[mode[0]], $num.int);
            $num[mode[0]].compact.flat = compactFormatter(compactFlat, mode[2], $num[mode[0]].flat, $num.int.flat);
            $num[mode[0]].compact.sign = compactFormatter(compactSign, mode[2], $num[mode[0]].sign, $num.int.sign);
        });

        const formatBytes = [
            'byte',
            'kilobyte',
            'megabyte',
            'gigabyte',
            'terabyte',
            'petabyte'
        ].map(unit => new Intl.NumberFormat(language, {
            numberingSystem: 'latn',
            style: 'unit',
            unit
        }));

        $num.bytes = function(num) {
            let magnitude = 0;
            while (formatBytes[magnitude + 1] && num >= 999.5) {
                num /= 1024;
                magnitude++;
            }
            return formatBytes[magnitude].format(num.toFixed(num < 10));
        }

        /**
         * Rudimentary (safe) postprocessing of back end formatted numbers to local format.
         */
        const group = $locale.number.group;
        const decimal = $locale.number.decimal;
        const percentSign = $locale.number.percentSign;
        const rtl = $num.int(-1).includes('\u200e');
        $num.raw = rtl
            ? num => num === void 0 ? emDash : (''+num)
                .replace(/\u200e?(['+-])/g, '\u200e$1')
                .replace(/%/, percentSign)
            : group == ',' && decimal == '.'
                ? num => num === void 0 ? emDash : (''+num)
                    .replace(/%/, percentSign)
                : num => num === void 0 ? emDash : (''+num)
                    .replace(/[.,]/g, ch => ch == '.' ? decimal : group)
                    .replace(/%/, percentSign);
    }
};


export default $num;
